import { useEffect, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { BrandMark } from "../components/BrandMark";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
    const { login, user, logout } = useAuth();
    const { t, lang, setLang } = useLang();
    const nav = useNavigate();
    const loc = useLocation();
    const [sp] = useSearchParams();
    const reauth = sp.get("reauth") === "1";
    const deep = sp.get("deep") || "";
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    // If we arrived in explicit re-auth mode, drop any lingering session on
    // mount so the user is forced to enter credentials again.
    useEffect(() => {
        if (!reauth) return;
        (async () => {
            try { await logout(); } catch { /* ignore */ }
        })();
    }, [reauth, logout]);

    useEffect(() => {
        if (reauth) return; // never auto-skip during re-auth
        if (user && user.id) {
            if (user.must_change) {
                nav("/change-password", { replace: true });
                return;
            }
            const to = loc.state?.from?.pathname || "/";
            nav(to, { replace: true });
        }
    }, [user, nav, loc.state, reauth]);

    async function onSubmit(e) {
        e.preventDefault();
        setBusy(true);
        const res = await login(username.trim(), password);
        setBusy(false);
        if (res.ok) {
            if (res.must_change) {
                toast.info("Please set a new password to continue");
                nav("/change-password", { replace: true });
            } else {
                toast.success("Welcome back");
                // If a notification deep-link is pending, honor it.
                if (deep) {
                    window.location.replace(deep);
                } else {
                    nav("/", { replace: true });
                }
            }
        } else {
            toast.error(res.error || "Login failed");
        }
    }

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
            {/* Left visual */}
            <div className="hidden lg:flex relative overflow-hidden">
                <img
                    src="https://images.unsplash.com/photo-1551766472-62096056b476?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwYXJjaGl0ZWN0dXJlJTIwZ2xhc3MlMjBidWlsZGluZyUyMGNsZWFyJTIwc2t5fGVufDB8fHx8MTc3OTE5NjQwNHww&ixlib=rb-4.1.0&q=85"
                    alt="building"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-950/70 via-blue-900/40 to-transparent" />
                <div className="relative z-10 p-12 flex flex-col justify-end text-white">
                    <div className="mb-6">
                        <BrandMark size={72} variant="white" />
                    </div>
                    <h1 className="font-display text-4xl font-bold leading-tight">
                        Modern microfinance,
                        <br /> built for the field.
                    </h1>
                    <p className="mt-3 text-blue-100 max-w-md">
                        Track loans, collect EMIs, approve in real-time. Designed for clarity.
                    </p>
                </div>
            </div>

            {/* Right form */}
            <div className="flex items-center justify-center p-6">
                <Card className="w-full max-w-md p-8 rounded-2xl border-slate-100 shadow-sm">
                    <div className="flex justify-end mb-2">
                        <div className="inline-flex rounded-lg bg-slate-100 p-1 text-xs">
                            <button
                                onClick={() => setLang("en")}
                                data-testid="login-lang-en"
                                className={`px-2.5 py-1 rounded-md ${lang === "en" ? "bg-white shadow-sm font-semibold" : "text-slate-500"}`}
                            >
                                EN
                            </button>
                            <button
                                onClick={() => setLang("mr")}
                                data-testid="login-lang-mr"
                                className={`px-2.5 py-1 rounded-md ${lang === "mr" ? "bg-white shadow-sm font-semibold" : "text-slate-500"}`}
                            >
                                मर
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 mb-5 lg:hidden">
                        <BrandMark size={44} />
                    </div>

                    <div className="flex items-center gap-2 text-blue-900 mb-1">
                        <ShieldCheck className="h-5 w-5" strokeWidth={1.75} />
                        <span className="text-xs uppercase tracking-widest font-semibold">
                            {t("admin_employee_login")}
                        </span>
                    </div>
                    <h2 className="font-display text-3xl font-bold text-slate-900">
                        {t("login")}
                    </h2>
                    <p className="text-sm text-slate-500 mt-1.5">{t("app_name")}</p>

                    <form onSubmit={onSubmit} className="mt-7 space-y-4">
                        <div className="space-y-1.5">
                            <Label htmlFor="username">{t("username")}</Label>
                            <Input
                                id="username"
                                data-testid="login-username-input"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder="admin"
                                className="h-12 rounded-xl"
                                autoComplete="username"
                                required
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="password">{t("password")}</Label>
                            <Input
                                id="password"
                                data-testid="login-password-input"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                className="h-12 rounded-xl"
                                autoComplete="current-password"
                                required
                            />
                        </div>
                        <Button
                            type="submit"
                            data-testid="login-submit-btn"
                            disabled={busy}
                            className="w-full h-12 rounded-xl bg-blue-900 hover:bg-blue-800 text-white font-semibold tap"
                        >
                            {busy ? "Signing in…" : t("sign_in")}
                        </Button>
                    </form>
                </Card>
            </div>
        </div>
    );
}
